'use client';

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface FooterButton {
  label: string | React.ReactNode;
  isToggle?: boolean;
  id?: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface CustomPopoverProps {
  id: string;
  header: string;
  body: string | React.ReactNode;
  footerBtns?: FooterButton[];
  hideCloseBtn?: boolean;
  maxWidth?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CustomPopover({
  id,
  header,
  body,
  footerBtns = [],
  hideCloseBtn = false,
  maxWidth,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CustomPopoverProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const prevControlledRef = React.useRef(controlledOpen !== undefined);
  
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange || (() => {}) : setInternalOpen;

  // Reset internal state only when transitioning from controlled to uncontrolled (e.g. on retake) - avoids resetting on every render when uncontrolled
  React.useEffect(() => {
    const wasControlled = prevControlledRef.current;
    const nowControlled = controlledOpen !== undefined;
    if (wasControlled && !nowControlled) {
      setInternalOpen(false);
    }
    prevControlledRef.current = nowControlled;
  }, [controlledOpen]);

  React.useEffect(() => {
    if (!isControlled) {
      // Listen for toggle-popover events
      const handleToggleEvent = (event: Event) => {
        const customEvent = event as CustomEvent<{ id: string }>;
        if (customEvent.detail?.id === id) {
          setInternalOpen((prev) => !prev);
        }
      };

      window.addEventListener('toggle-popover', handleToggleEvent);
      return () => {
        window.removeEventListener('toggle-popover', handleToggleEvent);
      };
    }
  }, [id, isControlled]);

  const handleToggle = () => {
    if (!isControlled) {
      setInternalOpen(!internalOpen);
    } else if (controlledOnOpenChange) {
      controlledOnOpenChange(!open);
    }
  };

  const handleButtonClick = (btn: FooterButton) => {
    if (btn.onClick) {
      btn.onClick();
    }
    if (btn.isToggle) {
      handleToggle();
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className={`!gap-0 w-full max-w-[calc(100%-2rem)] sm:!max-w-[720px]`}
        showCloseButton={!hideCloseBtn}
      >
        <DialogHeader className="p-4 bg-surface-3 !text-left">
          <DialogTitle className="cs-h4 text-text-default-strong">{header}</DialogTitle>
        </DialogHeader>
        <div
          className="cs-m-body-light px-4 py-6 bg-surface-2"
          dangerouslySetInnerHTML={
            typeof body === 'string' ? { __html: body } : undefined
          }
        >
          {typeof body !== 'string' ? body : null}
        </div>
        {footerBtns.length > 0 && (
          <DialogFooter className="!flex !flex-row !justify-end p-4 bg-surface-3">
            {footerBtns.map((btn, index) => (
              <Button
                key={index}
                variant={btn.className?.includes('outline') || btn.className?.includes('admy-btn-hover-underline') ? 'DocSecondaryBorder' : 'PrimaryFillButton'}
                onClick={() => handleButtonClick(btn)}
                disabled={btn.disabled}
                className={btn.className}
              >
                {btn.label}
              </Button>
            ))}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Global function to toggle popover (for backward compatibility)
export function BSToggle(id: string, options?: { backdrop?: string }) {
  const event = new CustomEvent('toggle-popover', { detail: { id, options } });
  window.dispatchEvent(event);
}

